import { useState, useEffect } from 'react';
import useDebounce from '../hooks/useDebounce';
import {
    Box, Heading, Table, Thead, Tbody, Tr, Th, Td,
    Button, HStack, useToast, Spinner, Center,
    Card, CardBody, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Input, Select, useDisclosure,
    InputGroup, InputLeftElement, IconButton, Flex,
    VStack, Text, Badge, Tooltip, TableContainer, Checkbox
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiClock, FiX, FiChevronLeft, FiChevronRight, FiUsers, FiArchive, FiRotateCcw } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ManageClassStudentsModal from '../components/ManageClassStudentsModal';
import ClassFormModal from '../components/ClassFormModal';
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';

const ClassesPage = () => {
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]); // 儲存所有老師
    const [institutions, setInstitutions] = useState([]); // 儲存機構 (Super Admin only)
    const [loading, setLoading] = useState(true);

    // Client-Side Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20; // 班級通常較少，20筆一頁

    // Class Modal
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Schedule Modal
    const { isOpen: isScheduleOpen, onOpen: onScheduleOpen, onClose: onScheduleClose } = useDisclosure();
    const [scheduleClass, setScheduleClass] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [exceptions, setExceptions] = useState([]);
    const [newSchedule, setNewSchedule] = useState({ day: 0, start: '14:00', end: '16:00' });
    const [newException, setNewException] = useState({ date: '', type: 'SKIP', start: '14:00', end: '16:00', note: '' });
    const [scheduleLoading, setScheduleLoading] = useState(false);

    // Students Modal
    const { isOpen: isStudentsOpen, onOpen: onStudentsOpen, onClose: onStudentsClose } = useDisclosure();
    const [studentsClass, setStudentsClass] = useState(null);

    // Archive-with-students confirm Modal（封存班級時，若有學生會變成無班級，跳出確認）
    const { isOpen: isArchiveOpen, onOpen: onArchiveOpen, onClose: onArchiveClose } = useDisclosure();
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [archivePreview, setArchivePreview] = useState({ orphan_students: [], cross_class_count: 0 });
    const [selectedOrphanIds, setSelectedOrphanIds] = useState([]);
    const [archiveLoading, setArchiveLoading] = useState(false);

    const handleOpenStudents = (cls) => {
        setStudentsClass(cls);
        onStudentsOpen();
    };

    const handleStudentCountChange = (classId, newCount) => {
        setClasses(prev => prev.map(c =>
            c.id === classId ? { ...c, student_count: newCount } : c
        ));
    };

    const { user } = useAuth(); // 取得當前使用者資訊
    const toast = useToast();

    // Client-side filtering (Move to top)
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const [filterTeacherId, setFilterTeacherId] = useState('');

    // Reset pagination
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, filterTeacherId]);

    const [editClass, setEditClass] = useState(null);

    // 用於上方的篩選選單 (Super Admin)
    // 用於上方的篩選選單 (Super Admin)
    const [filterInstId, setFilterInstId] = useState('');
    const [filterDepartment, setFilterDepartment] = useState(''); // Phase 22: Department Filter
    const [showArchived, setShowArchived] = useState(false); // 封存班級預設隱藏

    const [departments, setDepartments] = useState([]); // Hybrid list

    // Fixed definitions
    const FIXED_DEPARTMENTS = [
        { value: 'AfterSchool', label: '全科班' },
        { value: 'English', label: '英文部' },
        { value: 'Talent', label: '才藝部' },
    ];

    const getDepartmentLabel = (val) => {
        // Try to find in our hybrid list first, or fallback to fixed, or raw
        const found = departments.find(d => d.value === val) || FIXED_DEPARTMENTS.find(d => d.value === val);
        return found ? found.label : val || '-';
    };

    const fetchData = async () => {
        try {
            setLoading(true);

            // 決定要不要帶參數
            const params = { limit: 1000, include_archived: showArchived }; // Ensure we get all classes for client filtering
            if (user?.role === 'super_admin' && filterInstId) {
                params.institution_id = filterInstId;
            }

            const promises = [authAPI.getClasses(params)];

            // 只有管理員有權限取得所有使用者列表 (for Teacher Filter)
            const canFetchUsers = ['super_admin', 'admin'].includes(user?.role);
            if (canFetchUsers) {
                promises.push(authAPI.getUsers({
                    role_exclude: 'parent',
                    limit: 1000,
                }));
            }

            const [classesRes, usersRes] = await Promise.all(promises);
            setClasses(filterClassesForStaffUser(classesRes.data, user));

            // Hybrid Department Logic
            const foundDepts = [...new Set(classesRes.data.map(c => c.department).filter(Boolean))];
            const deptMap = new Map();
            FIXED_DEPARTMENTS.forEach(d => deptMap.set(d.value, d));
            foundDepts.forEach(dVal => {
                if (!deptMap.has(dVal)) {
                    deptMap.set(dVal, { value: dVal, label: dVal });
                }
            });
            setDepartments(Array.from(deptMap.values()));


            // 處理老師列表 (僅當有權限取得時)
            if (usersRes) {
                const staffList = usersRes.data
                    .filter(u => ['teacher', 'admin', 'receptionist'].includes(u.role))
                    .sort((a, b) => a.full_name.localeCompare(b.full_name, 'zh-Hant'));
                setTeachers(staffList);
            }

            // 如果是超級管理員，獲取機構列表
            if (user?.role === 'super_admin') {
                const instRes = await authAPI.getInstitutions();
                setInstitutions(instRes.data);
            }

        } catch (error) {
            console.error(error);
            toast({
                title: "無法取得資料",
                status: "error",
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // 當 filterInstId / showArchived 改變時，重新 fetch
    useEffect(() => {
        fetchData();
    }, [user, filterInstId, showArchived]);


    const [processingId, setProcessingId] = useState(null);

    const handleDelete = async (id) => {
        if (!window.confirm("確定要刪除這個班級嗎？")) return;
        setProcessingId(id);
        try {
            await authAPI.deleteClass(id);
            // Local Update: Remove from list
            setClasses(prev => prev.filter(c => c.id !== id));
            toast({ title: "刪除成功", status: "success" });
        } catch (error) {
            const errorMessage = error.response?.data?.detail || "刪除失敗，請稍後再試";
            toast({
                title: "刪除失敗",
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setProcessingId(null);
        }
    };

    const handleEdit = (cls) => { setEditClass(cls); onOpen(); };

    const doSimpleArchive = async (cls) => {
        setProcessingId(cls.id);
        try {
            await authAPI.updateClass(cls.id, { is_archived: true });
            toast({ title: "已封存", status: "success" });
            if (!showArchived) {
                setClasses(prev => prev.filter(c => c.id !== cls.id));
            } else {
                setClasses(prev => prev.map(c => c.id === cls.id ? { ...c, is_archived: true } : c));
            }
        } catch (error) {
            toast({ title: "操作失敗", description: error.response?.data?.detail || "請稍後再試", status: "error" });
        } finally {
            setProcessingId(null);
        }
    };

    const handleToggleArchive = async (cls) => {
        const nextArchived = !cls.is_archived;

        // 取消封存：維持原本的簡單確認流程
        if (!nextArchived) {
            if (!window.confirm(`確定要取消封存「${cls.name}」嗎？`)) return;
            setProcessingId(cls.id);
            try {
                await authAPI.updateClass(cls.id, { is_archived: false });
                toast({ title: "已取消封存", status: "success" });
                setClasses(prev => prev.map(c => c.id === cls.id ? { ...c, is_archived: false } : c));
            } catch (error) {
                toast({ title: "操作失敗", description: error.response?.data?.detail || "請稍後再試", status: "error" });
            } finally {
                setProcessingId(null);
            }
            return;
        }

        // 封存：班級沒有在籍學生，直接走原本的簡單確認
        if (!cls.student_count) {
            if (!window.confirm(`確定要封存「${cls.name}」嗎？封存後不會出現在一般選單，但資料不會被刪除。`)) return;
            await doSimpleArchive(cls);
            return;
        }

        // 有學生 → 先查有哪些學生會變成無班級，決定要不要跳出移舊生的確認視窗
        setProcessingId(cls.id);
        try {
            const res = await authAPI.previewClassArchive(cls.id);
            const { orphan_students = [], cross_class_count = 0 } = res.data;

            if (orphan_students.length === 0) {
                // 班內學生都還在其他班級，這次封存不影響任何人 → 沿用簡單確認
                const msg = cross_class_count > 0
                    ? `確定要封存「${cls.name}」嗎？班內 ${cross_class_count} 位學生都還在其他班級，不受影響。`
                    : `確定要封存「${cls.name}」嗎？封存後不會出現在一般選單，但資料不會被刪除。`;
                setProcessingId(null);
                if (!window.confirm(msg)) return;
                await doSimpleArchive(cls);
                return;
            }

            // 有學生封存後會變成完全無班級 → 開確認視窗讓管理員決定
            setArchiveTarget(cls);
            setArchivePreview({ orphan_students, cross_class_count });
            setSelectedOrphanIds(orphan_students.map(s => s.id));
            setProcessingId(null);
            onArchiveOpen();
        } catch (error) {
            toast({ title: "查詢失敗", description: error.response?.data?.detail || "請稍後再試", status: "error" });
            setProcessingId(null);
        }
    };

    const handleArchiveOnly = async () => {
        // Modal 內「只封存班級（學生不動）」
        const cls = archiveTarget;
        onArchiveClose();
        setArchiveTarget(null);
        if (cls) await doSimpleArchive(cls);
    };

    const handleConfirmArchiveWithStudents = async () => {
        if (!archiveTarget) return;
        setArchiveLoading(true);
        try {
            await authAPI.archiveClassWithStudents(archiveTarget.id, selectedOrphanIds);
            toast({
                title: "已封存班級",
                description: selectedOrphanIds.length > 0 ? `已將 ${selectedOrphanIds.length} 位學生移入舊生` : undefined,
                status: "success"
            });
            if (!showArchived) {
                setClasses(prev => prev.filter(c => c.id !== archiveTarget.id));
            } else {
                setClasses(prev => prev.map(c => c.id === archiveTarget.id ? { ...c, is_archived: true } : c));
            }
            onArchiveClose();
            setArchiveTarget(null);
        } catch (error) {
            toast({ title: "操作失敗", description: error.response?.data?.detail || "請稍後再試", status: "error" });
        } finally {
            setArchiveLoading(false);
        }
    };

    // Schedule Handlers
    const handleOpenSchedule = async (cls) => {
        setScheduleClass(cls);
        setScheduleLoading(true);
        try {
            const res = await authAPI.getClass(cls.id);
            setSchedules(res.data.schedules || []);
            setExceptions(res.data.exceptions || []);
            onScheduleOpen();
        } catch (err) {
            console.error(err);
            toast({ title: "無法載入課表", status: "error" });
        } finally {
            setScheduleLoading(false);
        }
    };

    const handleAddSchedule = async () => {
        try {
            const res = await authAPI.addClassSchedule(scheduleClass.id, {
                day_of_week: parseInt(newSchedule.day),
                start_time: newSchedule.start,
                end_time: newSchedule.end
            });
            toast({ title: "已新增", status: "success" });
            // Local Update
            setSchedules(prev => [...prev, res.data]);
        } catch (e) {
            console.error(e);
            toast({ title: "新增失敗", description: e.response?.data?.detail, status: "error" });
        }
    };

    const handleDeleteSchedule = async (id) => {
        try {
            await authAPI.deleteClassSchedule(scheduleClass.id, id);
            toast({ title: "已刪除", status: "success" });
            // Local Update
            setSchedules(prev => prev.filter(s => s.id !== id));
        } catch (e) {
            toast({ title: "刪除失敗", status: "error" });
        }
    };

    const handleAddException = async () => {
        if (!newException.date) {
            toast({ title: "請選擇日期", status: "warning" });
            return;
        }
        try {
            const res = await authAPI.addClassException(scheduleClass.id, {
                date: newException.date,
                type: newException.type,
                start_time: newException.type === 'ADD' ? newException.start : null,
                end_time: newException.type === 'ADD' ? newException.end : null,
                note: newException.note
            });
            toast({ title: "已新增例外", status: "success" });
            // Local Update
            setExceptions(prev => [...prev, res.data]);
        } catch (e) {
            toast({ title: "新增失敗", description: e.response?.data?.detail, status: "error" });
        }
    };

    const handleDeleteException = async (id) => {
        try {
            await authAPI.deleteClassException(scheduleClass.id, id);
            toast({ title: "已刪除例外", status: "success" });
            // Local Update
            setExceptions(prev => prev.filter(ex => ex.id !== id));
        } catch (e) {
            toast({ title: "刪除失敗", status: "error" });
        }
    };



    const filteredClasses = classes.filter(cls => {
        const matchesSearch = cls.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
        const matchesTeacher = filterTeacherId ? cls.teacher_id === parseInt(filterTeacherId) : true;
        const matchesDepartment = filterDepartment ? cls.department === filterDepartment : true;
        return matchesSearch && matchesTeacher && matchesDepartment;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filteredClasses.length / itemsPerPage) || 1;
    const paginatedClasses = filteredClasses.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const WEEKDAYS = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];

    return (
        <Flex direction="column" h="100%" w="100%" minW="0" overflow="hidden">
            <Box flexShrink={0} mb={4}>
                <HStack justifyContent="space-between" mb={6}>
                    <HStack>
                        <Heading size="lg" color="brand.800">班級管理</Heading>
                        {user?.role === 'super_admin' && (
                            <Select
                                placeholder="全部機構"
                                w="200px"
                                bg="white"
                                size="md"
                                value={filterInstId}
                                onChange={(e) => setFilterInstId(e.target.value)}
                            >
                                {institutions.map(inst => (
                                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                                ))}
                            </Select>
                        )}
                    </HStack>
                    {user?.role !== 'teacher' && (
                        <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={() => { setEditClass(null); onOpen(); }}>
                            新增班級
                        </Button>
                    )}
                </HStack>

                <HStack mb={4} spacing={4} wrap="wrap">
                    <InputGroup maxW="300px">
                        <InputLeftElement pointerEvents="none">
                            <FiSearch color="gray.300" />
                        </InputLeftElement>
                        <Input
                            placeholder="搜尋班級名稱..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            bg="white"
                        />
                    </InputGroup>

                    <Select
                        placeholder="所有部門"
                        maxW="150px"
                        bg="white"
                        value={filterDepartment}
                        onChange={(e) => setFilterDepartment(e.target.value)}
                    >
                        {departments.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                    </Select>

                    {['super_admin', 'admin'].includes(user?.role) && (
                        <Select
                            placeholder="所有老師"
                            maxW="200px"
                            bg="white"
                            value={filterTeacherId}
                            onChange={(e) => setFilterTeacherId(e.target.value)}
                        >
                            {teachers.map(t => (
                                <option key={t.id} value={t.id}>{t.full_name}</option>
                            ))}
                        </Select>
                    )}

                    <Checkbox
                        isChecked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                        colorScheme="brand"
                    >
                        顯示已封存班級
                    </Checkbox>
                </HStack>
            </Box>

            <Card w="100%" minW="0" flex="1" overflow="hidden" display="flex" flexDirection="column">
                <CardBody p={0} minW="0" overflow="hidden" display="flex" flexDirection="column">
                    <TableContainer flex="1" overflowY="auto" overflowX="auto" w="100%" maxW={{ base: 'calc(100vw - 32px)', md: '100%' }}>
                        <Table variant="simple" minW="800px" whiteSpace="nowrap">
                            <Thead bg="neutral.50" position="sticky" top={0} zIndex={1} shadow="sm">
                                <Tr>
                                    <Th whiteSpace="nowrap">班級名稱</Th>
                                    <Th whiteSpace="nowrap">部門</Th>
                                    <Th whiteSpace="nowrap">帶班老師</Th>
                                    <Th whiteSpace="nowrap">所屬機構</Th>
                                    <Th whiteSpace="nowrap">學生人數</Th>
                                    <Th whiteSpace="nowrap">操作</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {loading ? (
                                    <Tr>
                                        <Td colSpan={6}>
                                            <Center py={10}>
                                                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="brand.500" size="xl" />
                                            </Center>
                                        </Td>
                                    </Tr>
                                ) : (
                                    <>
                                        {paginatedClasses.map((cls) => (
                                            <Tr key={cls.id} opacity={cls.is_archived ? 0.55 : 1}>
                                                <Td fontWeight="bold" whiteSpace="nowrap">
                                                    <HStack spacing={2}>
                                                        <Text>{cls.name}</Text>
                                                        {cls.is_archived && <Badge colorScheme="gray">已封存</Badge>}
                                                    </HStack>
                                                </Td>
                                                <Td whiteSpace="nowrap">
                                                    <Badge colorScheme={
                                                        cls.department === 'English' ? 'purple' :
                                                            cls.department === 'Talent' ? 'orange' : 'blue'
                                                    }>
                                                        {getDepartmentLabel(cls.department)}
                                                    </Badge>
                                                </Td>
                                                <Td whiteSpace="nowrap">{cls.teacher ? cls.teacher.full_name : '未指派'}</Td>
                                                <Td whiteSpace="nowrap">{cls.institution ? cls.institution.name : '-'}</Td>
                                                <Td whiteSpace="nowrap">{cls.student_count || 0} 人</Td>
                                                <Td whiteSpace="nowrap">
                                                    <HStack spacing={2}>
                                                        <Tooltip label="管理學生" placement="top" hasArrow>
                                                            <IconButton
                                                                icon={<FiUsers />}
                                                                size="sm"
                                                                colorScheme="purple"
                                                                variant="ghost"
                                                                onClick={() => handleOpenStudents(cls)}
                                                                aria-label="管理學生"
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="設定課表" placement="top" hasArrow>
                                                            <IconButton
                                                                icon={<FiClock />}
                                                                size="sm"
                                                                colorScheme="blue"
                                                                variant="ghost"
                                                                onClick={() => handleOpenSchedule(cls)}
                                                                aria-label="課表"
                                                            />
                                                        </Tooltip>
                                                        {user?.role !== 'teacher' && (<>
                                                        <Tooltip label="編輯班級" placement="top" hasArrow>

                                                            <IconButton
                                                                icon={<FiEdit />}
                                                                size="sm"
                                                                colorScheme="teal"
                                                                variant="solid"
                                                                onClick={() => handleEdit(cls)}
                                                                aria-label="編輯"
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label={cls.is_archived ? "取消封存" : "封存班級"} placement="top" hasArrow>
                                                            <IconButton
                                                                icon={cls.is_archived ? <FiRotateCcw /> : <FiArchive />}
                                                                size="sm"
                                                                colorScheme="gray"
                                                                variant="outline"
                                                                onClick={() => handleToggleArchive(cls)}
                                                                isLoading={processingId === cls.id}
                                                                aria-label={cls.is_archived ? "取消封存" : "封存班級"}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="刪除班級" placement="top" hasArrow>
                                                            <IconButton
                                                                icon={<FiTrash2 />}
                                                                size="sm"
                                                                colorScheme="red"
                                                                variant="ghost"
                                                                onClick={() => handleDelete(cls.id)}
                                                                isLoading={processingId === cls.id}
                                                                aria-label="刪除"
                                                            />
                                                        </Tooltip>
                                                        </>)}
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                        {filteredClasses.length === 0 && (
                                            <Tr>
                                                <Td colSpan={6} textAlign="center" py={8} color="neutral.500">
                                                    尚無班級資料
                                                </Td>
                                            </Tr>
                                        )}
                                    </>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                    {/* Pagination Controls */}
                    {
                        totalPages > 1 && (
                            <Flex p={4} justify="space-between" align="center" borderTop="1px solid" borderColor="gray.100">
                                <Text fontSize="sm" color="gray.600">
                                    顯示第 {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredClasses.length)} 筆，共 {filteredClasses.length} 筆
                                </Text>
                                <HStack spacing={2}>
                                    <Button
                                        size="sm"
                                        leftIcon={<FiChevronLeft />}
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        isDisabled={currentPage === 1}
                                        variant="outline"
                                    >
                                        上一頁
                                    </Button>
                                    <Button size="sm" isDisabled _disabled={{ color: 'black', cursor: 'default', bg: 'gray.50' }} variant="ghost">
                                        {currentPage} / {totalPages}
                                    </Button>
                                    <Button
                                        size="sm"
                                        rightIcon={<FiChevronRight />}
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        isDisabled={currentPage === totalPages}
                                        variant="outline"
                                    >
                                        下一頁
                                    </Button>
                                </HStack>
                            </Flex>
                        )
                    }
                </CardBody>
            </Card>

            <ClassFormModal
                isOpen={isOpen}
                onClose={() => { setEditClass(null); onClose(); }}
                onSuccess={fetchData}
                teachers={teachers}
                institutions={institutions}
                departments={departments}
                user={user}
                editData={editClass}
            />

            {/* Schedule Modal */}
            <Modal isOpen={isScheduleOpen} onClose={onScheduleClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        課表管理
                        {scheduleClass && <Text fontSize="md" color="gray.500" fontWeight="normal">{scheduleClass.name}</Text>}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {scheduleLoading ? <Center py={10}><Spinner /></Center> : (
                            <VStack spacing={6} align="stretch">
                                <Box>
                                    <HStack mb={4} justify="space-between" align="flex-end">
                                        <HStack>
                                            <FormControl w="120px">
                                                <FormLabel fontSize="sm">星期</FormLabel>
                                                <Select size="sm" value={newSchedule.day} onChange={e => setNewSchedule({ ...newSchedule, day: parseInt(e.target.value) })}>
                                                    {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                                                </Select>
                                            </FormControl>
                                            <FormControl w="280px">
                                                <FormLabel fontSize="sm">時間</FormLabel>
                                                <HStack>
                                                    <Input type="time" size="sm" value={newSchedule.start} onChange={e => setNewSchedule({ ...newSchedule, start: e.target.value })} />
                                                    <Text>-</Text>
                                                    <Input type="time" size="sm" value={newSchedule.end} onChange={e => setNewSchedule({ ...newSchedule, end: e.target.value })} />
                                                </HStack>
                                            </FormControl>
                                        </HStack>
                                        <Button size="sm" colorScheme="blue" leftIcon={<FiPlus />} onClick={handleAddSchedule}>
                                            新增時段
                                        </Button>
                                    </HStack>

                                    <Heading size="sm" mb={3} borderBottom="1px" borderColor="gray.100" pb={2}>每週課表</Heading>

                                    {schedules.length === 0 ? (
                                        <Text color="gray.400" fontSize="sm">尚未設定常態課表</Text>
                                    ) : (
                                        <Table size="sm" variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th>星期</Th>
                                                    <Th>時段</Th>
                                                    <Th w="50px"></Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {schedules.sort((a, b) => a.day_of_week - b.day_of_week).map(s => (
                                                    <Tr key={s.id}>
                                                        <Td>
                                                            <Badge colorScheme="purple">{WEEKDAYS[s.day_of_week]}</Badge>
                                                        </Td>
                                                        <Td fontWeight="medium">
                                                            {s.start_time.toString().substring(0, 5)} - {s.end_time.toString().substring(0, 5)}
                                                        </Td>
                                                        <Td>
                                                            <IconButton
                                                                icon={<FiTrash2 />}
                                                                size="xs"
                                                                colorScheme="red"
                                                                variant="ghost"
                                                                onClick={() => handleDeleteSchedule(s.id)}
                                                            />
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    )}
                                </Box>

                                <Box pt={4} borderTop="1px" borderColor="gray.100">
                                    <Heading size="sm" mb={3} color="gray.600">例外日期 (調課/停課)</Heading>

                                    <HStack mb={4} align="flex-end" spacing={2} wrap="wrap">
                                        <FormControl w="140px">
                                            <FormLabel fontSize="sm" m={0}>日期</FormLabel>
                                            <Input type="date" size="sm" value={newException.date} onChange={e => setNewException({ ...newException, date: e.target.value })} />
                                        </FormControl>
                                        <FormControl w="90px">
                                            <FormLabel fontSize="sm" m={0}>類型</FormLabel>
                                            <Select size="sm" value={newException.type} onChange={e => setNewException({ ...newException, type: e.target.value })}>
                                                <option value="SKIP">停課</option>
                                                <option value="ADD">加課</option>
                                            </Select>
                                        </FormControl>
                                        {newException.type === 'ADD' && (
                                            <FormControl w="280px">
                                                <FormLabel fontSize="sm" m={0}>時間</FormLabel>
                                                <HStack spacing={1}>
                                                    <Input type="time" size="sm" value={newException.start} onChange={e => setNewException({ ...newException, start: e.target.value })} />
                                                    <Text>-</Text>
                                                    <Input type="time" size="sm" value={newException.end} onChange={e => setNewException({ ...newException, end: e.target.value })} />
                                                </HStack>
                                            </FormControl>
                                        )}
                                        <FormControl flex={1} minW="120px">
                                            <FormLabel fontSize="sm" m={0}>備註</FormLabel>
                                            <Input size="sm" placeholder="說明" value={newException.note} onChange={e => setNewException({ ...newException, note: e.target.value })} />
                                        </FormControl>
                                        <Button size="sm" colorScheme="orange" onClick={handleAddException}>新增</Button>
                                    </HStack>

                                    {exceptions.length > 0 && (
                                        <Table size="sm" variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th>日期</Th>
                                                    <Th>類型</Th>
                                                    <Th>說明</Th>
                                                    <Th w="50px"></Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {exceptions.sort((a, b) => new Date(a.date) - new Date(b.date)).map(ex => (
                                                    <Tr key={ex.id}>
                                                        <Td>{ex.date}</Td>
                                                        <Td>
                                                            <Badge colorScheme={ex.type === 'SKIP' ? 'red' : 'green'}>
                                                                {ex.type === 'SKIP' ? '停課' : `加課 ${ex.start_time?.substring(0, 5)}-${ex.end_time?.substring(0, 5)}`}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{ex.note}</Td>
                                                        <Td>
                                                            <IconButton
                                                                icon={<FiTrash2 />}
                                                                size="xs"
                                                                colorScheme="red"
                                                                variant="ghost"
                                                                onClick={() => handleDeleteException(ex.id)}
                                                            />
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    )}
                                </Box>

                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onScheduleClose}>關閉</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Students Modal */}
            <ManageClassStudentsModal
                isOpen={isStudentsOpen}
                onClose={onStudentsClose}
                classItem={studentsClass}
                onStudentCountChange={handleStudentCountChange}
            />

            {/* 封存班級：移舊生確認 Modal */}
            <Modal isOpen={isArchiveOpen} onClose={() => { onArchiveClose(); setArchiveTarget(null); }} size="lg" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>封存「{archiveTarget?.name}」</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack align="stretch" spacing={3}>
                            <Text>
                                以下 {archivePreview.orphan_students.length} 位學生在這個班沒有其他班級，
                                封存後將會變成完全沒有班級。是否一併將勾選的學生移入舊生？
                            </Text>
                            {archivePreview.cross_class_count > 0 && (
                                <Text fontSize="sm" color="gray.500">
                                    另有 {archivePreview.cross_class_count} 位學生還在其他班級，不受這次封存影響，不列於下方。
                                </Text>
                            )}
                            <Box maxH="260px" overflowY="auto" borderWidth="1px" borderRadius="md" p={3}>
                                <VStack align="stretch" spacing={2}>
                                    {archivePreview.orphan_students.map(s => (
                                        <Checkbox
                                            key={s.id}
                                            isChecked={selectedOrphanIds.includes(s.id)}
                                            onChange={(e) => {
                                                setSelectedOrphanIds(prev =>
                                                    e.target.checked
                                                        ? [...prev, s.id]
                                                        : prev.filter(id => id !== s.id)
                                                );
                                            }}
                                        >
                                            {s.full_name}
                                        </Checkbox>
                                    ))}
                                </VStack>
                            </Box>
                            <Text fontSize="sm" color="orange.600">
                                移入舊生會同時停用這些學生的感應卡/條碼，避免卡片誤刷。此動作可事後從「舊生」名單復學復原。
                            </Text>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => { onArchiveClose(); setArchiveTarget(null); }} isDisabled={archiveLoading}>
                            取消
                        </Button>
                        <Button variant="outline" mr={3} onClick={handleArchiveOnly} isDisabled={archiveLoading}>
                            只封存班級（學生不動）
                        </Button>
                        <Button colorScheme="brand" onClick={handleConfirmArchiveWithStudents} isLoading={archiveLoading}>
                            確認封存並移入舊生（{selectedOrphanIds.length}）
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default ClassesPage;
